'use client'

import { useHasCart } from '@/feature/cart/hooks/useHasCart'
import { useAddToCartTracking } from '@/feature/cart/services/cart.tracking'
import { trackAddToCartInLocalStorage } from '@/feature/cart/services/cart.tracking.addToCart'
import { useAddProductToCart } from '@/feature/product/hooks/useAddProductToCart'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Button } from '@/ui/buttons/button/Button.client'
import { QuantityPicker } from '@/ui/form/inputs/quantity-picker/QuantityPicker.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { useToast } from '@/ui/toast/Toast'
import { createNativeToastIntent } from '@/ui/toast/toast.intents'
import {
  type ProductBuyBox_CartFragment,
  type ReferencesProductTable_ArticleFragment,
  type ReferencesProductTableRows_PriceAndStockFragment,
} from '@gql-generated/graphql'
import { atom, useAtom } from 'jotai'
import { memo, useState } from 'react'
import { ProductPriceLoginLink } from '../../../product-price-login-link/ProductPriceLoginLink'
// TODO: Move it to the store when the components are split into multiple files.
const isAddingArticleToCartAtom = atom(false)
type ReferencesProductTableAddToCartProps = {
  /** Cart ID */
  cart?: ProductBuyBox_CartFragment
  /** Current article */
  article: ReferencesProductTable_ArticleFragment & ReferencesProductTableRows_PriceAndStockFragment
  /** Callback when quantity changes */
  onQuantityChange?: (quantity: number) => void
  /** Whether this is in a mono-discriminant context */
  isMonoDiscriminant?: boolean
}

function ReferencesProductTableAddToCartComponent({
  cart,
  article,
  onQuantityChange,
  isMonoDiscriminant = false,
}: ReferencesProductTableAddToCartProps): React.ReactElement | null {
  const isAuthenticated = useIsAuthenticated()
  const { t } = useTranslation(['referencesProductTable', 'generic'])
  const pushAddToCartEvent = useAddToCartTracking()
  const [addProductToCart, { loading: isAddingToCart }] = useAddProductToCart()
  const [quantitySelected, setQuantitySelected] = useState(
    article.stock?.packaging?.minimalSalesQuantity ?? 1,
  )
  const toast = useToast()
  const [isAddingArticleToCart, setIsAddingArticleToCart] = useAtom(isAddingArticleToCartAtom)
  const cartId = cart?.id
  const hasCart = useHasCart(cartId)

  const handleAddToCartSubmit = () => {
    if (!hasCart || !article.canAddToCart) {
      return
    }

    setIsAddingArticleToCart(true)
    addProductToCart({
      variables: {
        reference: article.reference,
        quantity: quantitySelected,
        cartId,
      },
      onCompleted: () => {
        handleIntent(
          createNativeToastIntent('success', `add-to-cart-${article.reference}`, {
            title: t('generic:addToCart.success.title'),
            message: t('generic:addToCart.success.message'),
          }),
          {
            whenInBrowser: () => {
              // Determine source based on discriminant type
              const source = isMonoDiscriminant
                ? 'fiche produit mono-discriminant - tableau de références'
                : 'fiche produit multi-discriminants - tableau de références'
              // Track the add to cart action
              trackAddToCartInLocalStorage([article.reference], source)
              pushAddToCartEvent([
                {
                  reference: article.reference,
                  quantity: quantitySelected,
                  source,
                  price: (article.price?.exclTax ?? 0) * quantitySelected,
                },
              ])
              toast.success({
                dedupe: false,
                title: t('generic:addToCart.success.title'),
                message: t('generic:addToCart.success.message'),
              })
            },
          },
        )
        setIsAddingArticleToCart(false)
      },
    })
  }

  if (!isAuthenticated) {
    return (
      <div className="desktop:text-right">
        <ProductPriceLoginLink className="desktop:justify-end" origin="references-product-table" />
      </div>
    )
  }

  if (!article.canAddToCart) {
    return null
  }

  return (
    <div className="flex gap-2 desktop:justify-end desktop:gap-1 desktop:text-right">
      <QuantityPicker
        value={quantitySelected}
        onQuantityChange={(quantity) => {
          setQuantitySelected(quantity)
          onQuantityChange?.(quantity)
        }}
        size="sm"
        maxQuantity={article.stock?.maxQuantity}
        intent={INTENT_SAME_AS_WEB}
        step={
          article.stock?.packaging?.incrementQuantity ?? article.stock?.packaging?.quantity ?? 1
        }
        minQuantity={article.stock?.packaging?.minimalSalesQuantity}
      />
      <span className="hidden desktop:flex">
        <Button
          size="md"
          variant="primary"
          isRound
          iconLeft={<Icon.ShoppingBasket />}
          onPress={handleAddToCartSubmit}
          isLoading={isAddingToCart}
          isDisabled={!isAddingToCart && isAddingArticleToCart}
        >
          {t('generic:addToCart.ctaLabel')}
        </Button>
      </span>
      <span className="flex min-w-0 flex-1 grow desktop:hidden">
        <Button
          size="md"
          className="!h-auto w-full"
          variant="primary"
          iconLeft={<Icon.ShoppingBasket />}
          onPress={handleAddToCartSubmit}
          isLoading={isAddingToCart}
          isDisabled={!isAddingToCart && isAddingArticleToCart}
          data-testid="referencesProductTable-cta-add-to-cart"
          intent={INTENT_SAME_AS_WEB}
        >
          {t('generic:addToCart.ctaLabel')}
        </Button>
      </span>
    </div>
  )
}

export const ReferencesProductTableAddToCart = memo(ReferencesProductTableAddToCartComponent)
